import React, { useState, useEffect, useCallback, useRef } from "react";
import { useMeetingAppContext } from "../../MeetingAppContextDef";
import { useSiteContext } from "../../context/SiteContext";
import { useMeetingState } from "../../context/MeetingStateContext";

import { usePubSub } from "../../config/sdkConfig";

import useIsMobile from "../../hooks/useIsMobile";
import { generateCTAHtml } from "../studio/cta/CTARenderer";
import { VerticalTemplates } from "../../services/Templates";
import { X } from "lucide-react";

const CtaSection = ({ sideBarContainerWidth, leftSidebarCollapsed = true, rightSidebarCollapsed = true }) => {
  const {
    activeElement,
    setActiveElement,
    meetingRole,
  } = useSiteContext();
  const { sideBarMode } = useMeetingAppContext();
  const meetingState = useMeetingState();
  const { publish: publishCloseElementSection } = usePubSub(
    "CLOSE_ELEMENT_SECTION"
  );
  const { publish: publishShowElement } = usePubSub("SHOW_ELEMENT_SECTION");
  const isMobile = useIsMobile();

  // Parse settings - build from multiple sources for maximum reliability
  let parsedFromSettings = {};
  if (activeElement.settings) {
    try {
      parsedFromSettings = typeof activeElement.settings === 'string'
        ? JSON.parse(activeElement.settings)
        : activeElement.settings;
    } catch (e) {
      console.error('[CtaSection] Failed to parse settings:', e);
    }
  }

  const settings = {
    ...parsedFromSettings,
    ...(activeElement.templateId && { templateId: activeElement.templateId }),
    ...(activeElement.displayMode && { displayMode: activeElement.displayMode }),
    ...(activeElement.position && { position: activeElement.position }),
    ...(activeElement.headline && { headline: activeElement.headline }),
    ...(activeElement.tagline && { tagline: activeElement.tagline }),
    ...(activeElement.bodyText && { bodyText: activeElement.bodyText }),
    ...(activeElement.buttons && { buttons: activeElement.buttons }),
    ...(activeElement.secondaryButton && { secondaryButton: activeElement.secondaryButton }),
    ...(activeElement.image && { image: activeElement.image }),
    ...(activeElement.background && { background: activeElement.background }),
    ...(activeElement.pricing && { pricing: activeElement.pricing }),
    ...(activeElement.listItems && { listItems: activeElement.listItems }),
    ...(activeElement.eventDetails && { eventDetails: activeElement.eventDetails }),
    ...(activeElement.animation && { animation: activeElement.animation }),
    ...(activeElement.padding && { padding: activeElement.padding }),
    ...(activeElement.maxWidth && { maxWidth: activeElement.maxWidth }),
    ...(activeElement.backdropBlur && { backdropBlur: activeElement.backdropBlur }),
  };

  // Detect old-format CTA
  const isOldFormat = activeElement.displayStyle && VerticalTemplates[activeElement.displayStyle];
  const OldCtaComponent = isOldFormat ? VerticalTemplates[activeElement.displayStyle]?.preview : null;

  const displayMode = settings.displayMode || 'vertical';
  const bannerPosition = settings.position || settings.placement || "left";

  // Animation state
  const [animPhase, setAnimPhase] = useState('entering');
  const closeTimerRef = useRef(null);
  const entryAnimation = settings.animation?.entry || 'fade';
  const exitAnimation = settings.animation?.exit || 'fade';
  const ANIMATION_DURATION = 500;

  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setAnimPhase('entered');
      });
    });
    return () => {
      cancelAnimationFrame(raf);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  const doClose = useCallback(() => {
    const isHostRole = meetingRole === "moderator" || meetingRole === "co-host";
    if (isHostRole) {
      const elementId = activeElement?._id || activeElement?.id;
      publishCloseElementSection(JSON.stringify({ set: true }));
      publishShowElement(JSON.stringify({ clear: true }), { persist: true });
      if (elementId) meetingState.emitElementRemove(elementId);
    }
    setActiveElement(null);
  }, [setActiveElement, meetingRole, publishCloseElementSection, publishShowElement, meetingState, activeElement]);

  const handleClose = useCallback(() => {
    if (exitAnimation === 'none') {
      doClose();
      return;
    }
    setAnimPhase('exiting');
    closeTimerRef.current = setTimeout(doClose, ANIMATION_DURATION);
  }, [exitAnimation, doClose, ANIMATION_DURATION]);

  // Animation helpers
  const getSlideTransform = (isOut) => {
    const distance = '40px';
    switch (bannerPosition) {
      case 'left': return `translateX(-${distance})`;
      case 'right': return `translateX(${distance})`;
      case 'top': return `translateY(-${distance})`;
      case 'bottom': return `translateY(${distance})`;
      case 'center':
      default: return `translateY(${isOut ? '' : '-'}${distance})`;
    }
  };

  const getAnimationStyle = () => {
    const timing = 'cubic-bezier(0.4, 0, 0.2, 1)';
    const bounceTiming = 'cubic-bezier(0.68, -0.55, 0.265, 1.55)';
    const dur = `${ANIMATION_DURATION}ms`;

    if (animPhase === 'entering') {
      switch (entryAnimation) {
        case 'none': return { opacity: 1 };
        case 'slide': return { opacity: 0, transform: getSlideTransform(false) };
        case 'zoom': return { opacity: 0, transform: 'scale(0.5)' };
        case 'bounce': return { opacity: 0, transform: 'scale(0.3)' };
        case 'fade':
        default: return { opacity: 0 };
      }
    }

    if (animPhase === 'entered') {
      const anim = entryAnimation === 'bounce' ? bounceTiming : timing;
      return {
        opacity: 1,
        transform: 'translateX(0) translateY(0) scale(1)',
        transition: `opacity ${dur} ${anim}, transform ${dur} ${anim}`,
      };
    }

    if (animPhase === 'exiting') {
      const anim = exitAnimation === 'bounce' ? bounceTiming : timing;
      const base = { transition: `opacity ${dur} ${anim}, transform ${dur} ${anim}` };
      switch (exitAnimation) {
        case 'none': return { opacity: 0 };
        case 'slide': return { ...base, opacity: 0, transform: getSlideTransform(true) };
        case 'zoom': return { ...base, opacity: 0, transform: 'scale(0.5)' };
        case 'bounce': return { ...base, opacity: 0, transform: 'scale(0.3)' };
        case 'fade':
        default: return { ...base, opacity: 0 };
      }
    }

    return {};
  };

  const stopPropagation = (e) => {
    e.stopPropagation();
  };

  const animStyle = getAnimationStyle();

  // Generate iframe HTML for new-format CTAs
  const html = !isOldFormat ? generateCTAHtml(settings, displayMode, isMobile) : '';

  // Close button component
  const closeButton = (
    <button
      onClick={handleClose}
      className="absolute -top-3 -right-3 z-10 w-7 h-7 bg-black/70 hover:bg-black/90 rounded-full flex items-center justify-center transition-colors shadow-lg border border-white/20"
    >
      <X className="w-4 h-4 text-white" />
    </button>
  );

  // Old format CTA - keep as fixed overlay for backward compatibility
  if (isOldFormat && OldCtaComponent) {
    return (
      <div
        className="fixed z-[25] left-4"
        style={{
          top: '72px',
          maxHeight: 'calc(100vh - 160px)',
          ...animStyle,
        }}
        onClick={stopPropagation}
        onMouseDown={stopPropagation}
        onContextMenu={stopPropagation}
      >
        <OldCtaComponent
          {...settings}
          inmeeting={true}
          position={bannerPosition}
          center={bannerPosition === "center"}
          onClose={handleClose}
        />
      </div>
    );
  }

  // --- New format: Same flex-sibling layout for both desktop and mobile ---

  // Vertical CTA (Left/Right Sidebar)
  if (displayMode === 'vertical') {
    return (
      <div
        className={`${bannerPosition === 'left' ? 'order-first' : 'order-last'} flex-shrink-0 h-full z-10 relative`}
        style={{
          width: isMobile ? 'clamp(140px, 42%, 220px)' : 'clamp(240px, 20%, 420px)',
          ...animStyle,
        }}
        onClick={stopPropagation}
        onMouseDown={stopPropagation}
        onContextMenu={stopPropagation}
      >
        {closeButton}
        <div
          className={`w-full h-full shadow-2xl ${bannerPosition === 'left' ? 'border-r' : 'border-l'} border-white/10`}
        >
          <iframe
            srcDoc={html}
            className="w-full h-full border-0"
            title="CTA Banner"
            sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
          />
        </div>
      </div>
    );
  }

  // Horizontal CTA (Top/Bottom Strip)
  if (displayMode === 'horizontal') {
    return (
      <div
        className={`${bannerPosition === 'top' ? 'order-first' : 'order-last'} flex-shrink-0 w-full z-10 relative`}
        style={{
          height: isMobile ? 'clamp(100px, 30%, 200px)' : 'clamp(120px, 20%, 250px)',
          ...animStyle,
        }}
        onClick={stopPropagation}
        onMouseDown={stopPropagation}
        onContextMenu={stopPropagation}
      >
        {closeButton}
        <div
          className={`w-full h-full shadow-2xl ${bannerPosition === 'top' ? 'border-b' : 'border-t'} border-white/10`}
        >
          <iframe
            srcDoc={html}
            className="w-full h-full border-0"
            title="CTA Banner"
            sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
          />
        </div>
      </div>
    );
  }

  // Centered CTA (Overlay Block)
  return (
    <div
      className="absolute inset-0 z-40 flex items-center justify-center p-2 sm:p-0"
      style={{
        backgroundColor: settings.backdropBlur ? 'rgba(0,0,0,0.5)' : 'transparent',
        backdropFilter: settings.backdropBlur ? 'blur(4px)' : 'none',
        ...animStyle,
      }}
      onClick={stopPropagation}
      onMouseDown={stopPropagation}
      onContextMenu={stopPropagation}
    >
      <div
        className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10"
        style={{
          width: isMobile ? 'clamp(260px, 95%, 400px)' : 'clamp(300px, 80%, 900px)',
          maxWidth: settings.maxWidth || 800,
          maxHeight: '80%',
        }}
      >
        {closeButton}
        <iframe
          srcDoc={html}
          className="w-full border-0"
          style={{ minHeight: isMobile ? 180 : 200 }}
          title="CTA Banner"
          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
        />
      </div>
    </div>
  );
};

export default CtaSection;
